import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import { Box, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { splitPoNumberDiff } from '@/utils/po-number-diff'
import { discrepancyBgColor } from './shared'

type MismatchedPoNumberProps = {
  value: string | null
  other: string | null
}

/** A PO number with the characters that differ from `other` marked. */
function MismatchedPoNumber({ value, other }: MismatchedPoNumberProps) {
  if (!value) {
    return (
      <Typography component="span" variant="body2" fontWeight={600}>
        —
      </Typography>
    )
  }

  // With nothing to compare against, the whole value is the difference.
  const { prefix, differing, suffix } = other
    ? splitPoNumberDiff(value, other)
    : { prefix: '', differing: value, suffix: '' }

  return (
    <Typography
      component="span"
      variant="body2"
      fontWeight={600}
      // Break anywhere so a PO number wider than the card wraps instead of
      // getting clipped (and hiding differing characters at its end).
      sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere', minWidth: 0 }}
    >
      {prefix}
      {differing && (
        <Box
          component="mark"
          data-testid="po-number-diff"
          sx={{
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.55),
            color: 'inherit',
            fontWeight: 800,
            borderRadius: 0.5,
            px: 0.25,
            textDecoration: 'underline',
            textDecorationThickness: 2,
            textUnderlineOffset: 3,
          }}
        >
          {differing}
        </Box>
      )}
      {suffix}
    </Typography>
  )
}

type PoNumberMismatchBannerProps = {
  invoicePoNumber: string | null
  poNumber: string | null
}

/**
 * Always-visible strip under a card's header for a PO number mismatch. Shows the
 * invoice's and the PO's numbers side by side with the differing characters
 * marked, so a near-identical pair (`PO-10234` vs `PO-10243`) can be told apart
 * at a glance instead of from a tooltip.
 */
export default function PoNumberMismatchBanner({
  invoicePoNumber,
  poNumber,
}: PoNumberMismatchBannerProps) {
  return (
    <Stack
      data-testid="po-number-mismatch-banner"
      direction="row"
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
      columnGap={2}
      rowGap={0.5}
      sx={{
        px: 3,
        py: 1,
        bgcolor: discrepancyBgColor('warning', 0.2),
        borderBottom: 2,
        borderColor: 'warning.main',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <WarningAmberRounded sx={{ fontSize: 20, color: 'warning.dark' }} />
        <Typography variant="body2" fontWeight={700}>
          PO numbers don&apos;t match
        </Typography>
      </Stack>
      <Stack
        direction="row"
        alignItems="baseline"
        useFlexGap
        flexWrap="wrap"
        columnGap={1}
        sx={{ ml: 'auto' }}
      >
        <Typography variant="caption" color="text.secondary">
          Invoice
        </Typography>
        <MismatchedPoNumber value={invoicePoNumber} other={poNumber} />
        <Typography
          variant="body2"
          fontWeight={700}
          color="warning.dark"
          aria-label="does not match"
          sx={{ mx: 0.5 }}
        >
          ≠
        </Typography>
        <Typography variant="caption" color="text.secondary">
          PO
        </Typography>
        <MismatchedPoNumber value={poNumber} other={invoicePoNumber} />
      </Stack>
    </Stack>
  )
}
